import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ZonesApiService } from '@simOn/zone/data-access';
import {
  CreateZoneDto,
  UpdateZoneDto,
  UpdateZoneRoomsOrderDto,
  UpdateZonesOrderDto,
  ZoneInterface
} from '@simOn/zone/models';
import { defer, exhaustMap, Observable, pipe, switchMap, tap } from 'rxjs';

export interface ZonesState {
  zones: ZoneInterface[];
  isLoaded: boolean;
  loading: boolean;
}

const initialState: ZonesState = {
  zones: [],
  isLoaded: false,
  loading: false
};

export const ZonesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    /** Zones the user can see, pick for a room and reorder; the technical unassigned zone is left out. */
    assignableZones: computed<ZoneInterface[]>(() => store.zones().filter((zone) => !zone.isUnassignedZone)),
    /** The technical zone that stores the order of the rooms without a zone; `null` until loaded. */
    unassignedZone: computed<ZoneInterface | null>(() => store.zones().find((zone) => zone.isUnassignedZone) ?? null)
  })),
  withMethods((store, api = inject(ZonesApiService)) => ({
    /** Zones belong to the selected space, so the cache is dropped together with the other stores on leaving it. */
    clearState: (): void => patchState(store, initialState),
    loadZones: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        switchMap(() =>
          api.getZones().pipe(
            tapResponse({
              next: (zones) => patchState(store, { zones: zones ?? [], isLoaded: true, loading: false }),
              error: (err) => {
                patchState(store, { loading: false });
                console.error(err);
              }
            })
          )
        )
      )
    ),
    /**
     * Unlike `loadZones` and the order methods this one returns the stream instead of swallowing it,
     * so callers can react to the outcome (e.g. show a toast) while the store still updates its state.
     */
    createZone: (dto: CreateZoneDto): Observable<ZoneInterface> =>
      defer(() => {
        patchState(store, { loading: true });
        return api.createZone(dto);
      }).pipe(
        tap({
          next: (zone) => patchState(store, { zones: [...store.zones(), zone], loading: false }),
          error: () => patchState(store, { loading: false })
        })
      ),
    /** Returns the stream so callers can report the outcome; see `createZone`. */
    updateZone: (dto: UpdateZoneDto): Observable<ZoneInterface> =>
      defer(() => {
        patchState(store, { loading: true });
        return api.updateZone(dto);
      }).pipe(
        tap({
          next: (updated) =>
            patchState(store, {
              zones: store.zones().map((zone) => (zone.id === updated.id ? updated : zone)),
              loading: false
            }),
          error: () => patchState(store, { loading: false })
        })
      ),
    /** Returns the stream so callers can report the outcome; see `createZone`. */
    deleteZone: (id: string): Observable<void> =>
      defer(() => {
        patchState(store, { loading: true });
        return api.deleteZone(id);
      }).pipe(
        tap({
          next: () => patchState(store, { zones: store.zones().filter((zone) => zone.id !== id), loading: false }),
          error: () => patchState(store, { loading: false })
        })
      ),
    /** Returns the stream so callers can report the outcome; see `createZone`. */
    reorderZones: (dto: UpdateZonesOrderDto): Observable<void> =>
      defer(() => {
        patchState(store, { loading: true });
        return api.updateZonesOrder(dto);
      }).pipe(
        tap({
          next: () => {
            const byId = new Map(store.zones().map((zone) => [zone.id, zone]));
            const ordered = dto.zoneIdsOrder
              .map((id) => byId.get(id))
              .filter((zone): zone is ZoneInterface => zone !== undefined);
            const rest = store.zones().filter((zone) => !dto.zoneIdsOrder.includes(zone.id));
            patchState(store, { zones: [...ordered, ...rest], loading: false });
          },
          error: () => patchState(store, { loading: false })
        })
      ),
    /** Returns the stream so callers can report the outcome; see `createZone`. */
    reorderZoneRooms: (dto: UpdateZoneRoomsOrderDto): Observable<void> =>
      defer(() => {
        patchState(store, { loading: true });
        return api.updateZoneRoomsOrder(dto);
      }).pipe(
        tap({
          next: () =>
            patchState(store, {
              zones: store
                .zones()
                .map((zone) => (zone.id === dto.zoneId ? { ...zone, roomsOrder: dto.roomIdsOrder } : zone)),
              loading: false
            }),
          error: () => patchState(store, { loading: false })
        })
      ),
    updateZonesOrder: rxMethod<UpdateZonesOrderDto>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        exhaustMap((dto) =>
          api.updateZonesOrder(dto).pipe(
            tapResponse({
              next: () => {
                const byId = new Map(store.zones().map((zone) => [zone.id, zone]));
                const ordered = dto.zoneIdsOrder
                  .map((id) => byId.get(id))
                  .filter((zone): zone is ZoneInterface => zone !== undefined);
                const rest = store.zones().filter((zone) => !dto.zoneIdsOrder.includes(zone.id));
                patchState(store, { zones: [...ordered, ...rest], loading: false });
              },
              error: (err) => {
                patchState(store, { loading: false });
                console.error(err);
              }
            })
          )
        )
      )
    ),
    updateZoneRoomsOrder: rxMethod<UpdateZoneRoomsOrderDto>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        exhaustMap((dto) =>
          api.updateZoneRoomsOrder(dto).pipe(
            tapResponse({
              next: () =>
                patchState(store, {
                  zones: store
                    .zones()
                    .map((zone) => (zone.id === dto.zoneId ? { ...zone, roomsOrder: dto.roomIdsOrder } : zone)),
                  loading: false
                }),
              error: (err) => {
                patchState(store, { loading: false });
                console.error(err);
              }
            })
          )
        )
      )
    )
  }))
);
